import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  jobPostingsApi,
  jobCategoriesApi,
  jobTagsApi,
} from "proxyUrl";

const InfoRow = ({ label, value }) => (
  <div className="space-y-1">
    <p className="text-xs font-semibold uppercase text-[#444]">{label}</p>
    <p className="rounded-md bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">
      {value || "-"}
    </p>
  </div>
);

const getDepartmentName = (departmentId) => {
  const departmentMap = {
    1: "VL",
    2: "Vnaya",
    3: "Creed Staffing",
    4: "VLS",
    5: "Aone FF",
    6: "Dispatch",
  };
  return departmentMap[departmentId] || null;
};

const JobPostingPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [comment, setComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  // Permissions
  const loggedUser = useSelector((state) => state.user.user || {});
  const userPermissions = loggedUser.userPermission || [];
  const canViewJob = userPermissions.includes("view Job posting");
  const canUpdateJob = userPermissions.includes("update Job posting");
  const canManageLeads =
    userPermissions.includes("create leads") ||
    userPermissions.includes("update leads") ||
    userPermissions.includes("delete leads");

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${jobPostingsApi}/${id}/preview`, {
        withCredentials: true,
      });
      setJob(data?.data || null);
    } catch (error) {
      console.error("jobPosting.preview ui error", error);
      toast.error("Unable to load job posting");
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxonomies = async () => {
    try {
      const [catRes, tagRes] = await Promise.all([
        axios.get(jobCategoriesApi, { withCredentials: true }),
        axios.get(jobTagsApi, { withCredentials: true }),
      ]);
      setCategories(catRes?.data?.data || []);
      setTags(tagRes?.data?.data || []);
    } catch (error) {
      console.error("job preview taxonomies error", error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }

    setSubmittingComment(true);
    try {
      const { data } = await axios.post(
        `${jobPostingsApi}/${id}/comment`,
        { comment: comment.trim() },
        { withCredentials: true }
      );

      if (data.message === "Comment added successfully") {
        toast.success("Comment added successfully");
        setComment("");
        // Refresh the preview to get updated comments
        await fetchPreview();
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to add comment";
      toast.error(errorMessage);
    } finally {
      setSubmittingComment(false);
    }
  };

  useEffect(() => {
    fetchPreview();
    fetchTaxonomies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-600">
        Loading...
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-600">
        Job posting not found
      </div>
    );
  }

  if (!canViewJob) {
    return (
      <div className="rounded-xl bg-white p-6 text-center text-sm text-red-600 shadow">
        You are not authorized to view job postings.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between rounded-2xl bg-indigo-600 p-4 text-white shadow">
        <div>
          <p className="text-xs uppercase tracking-wide opacity-80">
            Job posting preview
          </p>
          <h1 className="text-xl font-semibold">{job.title}</h1>
          <p className="text-xs opacity-80">
            Created {new Date(job.createdAt || job.updatedAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-2">
          {canManageLeads && (
            <button
              onClick={() => navigate(`/admin/job-postings/${id}/leads`)}
              className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-primaryBlue shadow hover:shadow-md"
            >
              View leads
            </button>
          )}
          {canUpdateJob && (
            <button
              onClick={() => navigate(`/admin/job-postings/${id}/edit`)}
              className="rounded-lg border border-white px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white p-6 shadow-sm">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <InfoRow label="Job title" value={job.title} />
              <InfoRow label="Location" value={job.location} />
              <InfoRow label="Job status" value={job.jobStatus} />
              <InfoRow label="Job type" value={job.jobType} />
              <InfoRow
                label="Assigned to"
                value={
                  job.assignedToName ||
                  job.assignedUser?.name ||
                  job.assignedUser?.email ||
                  (job.assignedTo ? `User #${job.assignedTo}` : "-")
                }
              />
              <InfoRow
                label="Job category"
                value={
                  categories.find((c) => c.id == job.jobCategoryId)?.name ||
                  categories.find((c) => c.id == job.jobCategoryId)?.title ||
                  job.jobCategory
                }
              />
              <InfoRow label="Total experience" value={job.totalExperience} />
              <InfoRow label="Employee type" value={job.employeeType} />
              <InfoRow
                label="Hiring for Department"
                value={
                  job.hiringForDepartment
                    ? getDepartmentName(Number(job.hiringForDepartment)) || "Not assigned"
                    : "Not assigned"
                }
              />
              <InfoRow
                label="Job tags"
                value={
                  Array.isArray(job.jobTagIds) && job.jobTagIds.length
                    ? job.jobTagIds
                        .map(
                          (t) =>
                            tags.find((tg) => tg.id === t)?.name ||
                            tags.find((tg) => tg.id === t)?.title ||
                            t
                        )
                        .join(", ")
                    : Array.isArray(job.jobTags)
                    ? job.jobTags.join(", ")
                    : job.jobTags || "-"
                }
              />
              <InfoRow
                label="Salary range"
                value={
                  job.minSalary || job.maxSalary
                    ? `${job.minSalary || "-"} - ${job.maxSalary || "-"}`
                    : "-"
                }
              />
            </div>

            <div>
              <p className="text-xs font-semibold uppercase text-[#444]">
                Description
              </p>
              <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                {job.description || "No description provided."}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase text-[#444]">
                Application questions
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(job.applicationQuestions || {})
                  .filter(([, value]) => value)
                  .map(([key]) => (
                    <span
                      key={key}
                      className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700"
                    >
                      {key.replace(/([A-Z])/g, " $1")}
                    </span>
                  ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase text-[#444]">
                Pipeline
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(job.pipeline || []).map((stage) => (
                  <span
                    key={stage.name}
                    className="rounded-full px-3 py-1 text-xs font-semibold text-white"
                    style={{ background: stage.color || "#0f766e" }}
                  >
                    {stage.name}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase text-[#444]">
                Automated emails
              </p>
              <div className="mt-2 space-y-2">
                {(job.automatedEmails || []).map((item) => (
                  <div
                    key={item.key}
                    className="rounded-lg border border-gray-200 p-3"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-800">
                        {item.label}
                      </p>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          item.enabled
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {item.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                    {item.subject && (
                      <p className="mt-2 text-xs text-gray-600">
                        Subject: {item.subject}
                      </p>
                    )}
                    {item.body && (
                      <p className="mt-1 text-xs text-gray-600">
                        {item.body}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase text-gray-500 mb-4">
                Comments
              </p>
              
              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="mb-6">
                <div className="space-y-3">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <button
                    type="submit"
                    disabled={submittingComment || !comment.trim()}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submittingComment ? "Adding..." : "Add Comment"}
                  </button>
                </div>
              </form>

              {/* Comments Timeline */}
              <div className="space-y-4">
                {job.comments && job.comments.length > 0 ? (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    
                    {/* Comments */}
                    <div className="space-y-6">
                      {job.comments.map((commentItem, index) => (
                        <div key={commentItem.id} className="relative flex gap-4">
                          {/* Timeline dot */}
                          <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100">
                            <div className="h-3 w-3 rounded-full bg-indigo-600"></div>
                          </div>
                          
                          {/* Comment content */}
                          <div className="flex-1 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <p className="text-sm font-semibold text-gray-800">
                                    {commentItem.name || "Unknown User"}
                                  </p>
                                  {commentItem.email && (
                                    <span className="text-xs text-gray-500">
                                      ({commentItem.email})
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                  {commentItem.comment}
                                </p>
                              </div>
                            </div>
                            <p className="mt-3 text-xs text-gray-500">
                              {new Date(commentItem.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                    No comments yet. Be the first to comment!
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1 space-y-4">
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-800">
                Leads overview
              </p>
              <p className="mt-2 text-3xl font-bold text-primaryBlue">
                {job.leads?.length || 0}
              </p>
              <p className="text-xs text-gray-500">Total leads</p>
              {canManageLeads && (
                <button
                  onClick={() => navigate(`/admin/job-postings/${id}/leads`)}
                  className="mt-3 w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white"
                >
                  Manage leads
                </button>
              )}
            </div>
            {job.featuredImage && (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <img
                  src={job.featuredImage}
                  alt="Featured"
                  className="h-48 w-full object-cover"
                />
              </div>
            )}
            <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-700">
              <p className="font-semibold">Meta</p>
              <pre className="mt-2 max-h-60 overflow-auto rounded bg-gray-50 p-2 text-xs">
                {JSON.stringify(job.meta || {}, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Link
          to="/admin/job-postings"
          className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
        >
          Back to list
        </Link>
        {canUpdateJob && (
          <button
            onClick={() => navigate(`/admin/job-postings/${id}/edit`)}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white"
          >
            Edit posting
          </button>
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default JobPostingPreview;

